import React, { ChangeEvent, useRef, useState } from 'react';
import { isEqual } from 'lodash';

import SearchInput from 'src/components/SearchInput';

import { ReactComponent as CheckMark } from '../../assets/svgs/checkmark.svg';
import { ReactComponent as IconDropDownArrow } from '../../assets/svgs/icon-dropdown-arrow.svg';
import { useOutsideClick } from '../../hooks';
import usePrevious from '../../hooks/usePrevious';

export interface Option {
  text: string;
  value: string;
}

interface Props {
  options: Option[];
  activeIndexes?: string[];
  placeholder: string;
  showsSearch?: boolean;
  searchTerm?: string;
  setActiveIndexes: (items: string[]) => void;
  onClear?: () => void;
  onSearch?: (searchString: string) => void;
  onChange: (items: string[]) => void;
  dataTestID?: string;
}

export enum MultiSelectDropDownTestID {
  ButtonContainer = 'ButtonContainer',
  SaveButton = 'SaveButton',
  ClearButton = 'ClearButton',
  MultiSelectSearchInput = 'MultiSelectSearchInput',
}

export const MultiSelectDropDown: React.FC<Props> = ({
  options,
  activeIndexes = [],
  placeholder,
  showsSearch = false,
  searchTerm = '',
  setActiveIndexes,
  // tslint:disable-next-line:no-empty
  onClear = () => {},
  // tslint:disable-next-line:no-empty
  onSearch = (searchString) => {},
  onChange,
  dataTestID = '',
}) => {
  const [showDropdown, setShowDropdown] = useState(false);

  const listRef = useRef(null);

  const previousActiveIndexes = usePrevious(activeIndexes);

  useOutsideClick(listRef, () => {
    if (!previousActiveIndexes || !isEqual(activeIndexes.sort(), previousActiveIndexes.sort())) {
      onChange(activeIndexes);
    }
    setShowDropdown(false);
  });

  const isSelectedItem: (someItem: string) => boolean = (someItem) => {
    return !!someItem && activeIndexes.includes(someItem);
  };

  const onSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value;
    onSearch(term);
  };

  return (
    <div className="z-30 relative h-10 my-3 mr-2" ref={listRef} data-testid={dataTestID}>
      {showDropdown && (
        <div className="absolute border border-gray-300 top-12 left-0 w-56 bg-white shadow-lg rounded max-h-100">
          {showsSearch && (
            <SearchInput
              isTall
              inputClassName="border rounded border-gray-300 m-2 w-11/12 bg-white t-0"
              onChange={onSearchChange}
              value={searchTerm}
              dataTestID={MultiSelectDropDownTestID.MultiSelectSearchInput}
            />
          )}
          <ul className="relative left-0 w-56 overflow-y-scroll max-h-72">
            {options.map((item, i) => (
              <li
                key={`${item.value} ${i}`}
                data-testid={`${dataTestID}Option${item.text}`}
                onClick={() => {
                  let updatedIndexes: string[] = [...activeIndexes];
                  const itemIndex: number = updatedIndexes.indexOf(item.value);
                  if (itemIndex !== -1) {
                    updatedIndexes.splice(itemIndex, 1);
                  } else {
                    updatedIndexes = [...updatedIndexes, item.value];
                  }
                  setActiveIndexes(updatedIndexes);
                }}
                className={`${
                  isSelectedItem(item.value)
                    ? 'text-blue-200 bg-blue-100'
                    : 'text-gray-600 hover:bg-gray-100'
                } justify-start items-center cursor-pointer font-medium p-3 text-left flex`}
              >
                <div
                  className={`${
                    isSelectedItem(item.value)
                      ? 'border-2 border-blue-200 bg-blue-200'
                      : 'border-2 border-gray-300 w-4 h-4'
                  } mr-3 rounded`}
                >
                  {isSelectedItem(item.value) && (
                    <CheckMark
                      aria-label={`checkmark for ${item.text}`}
                      className="w-3 h-3 fill-current text-black"
                    />
                  )}
                </div>
                {item.text}
              </li>
            ))}
          </ul>
          <div
            className="relative bottom-0 left-0 flex justify-between w-full p-3 border-b border-t rounded-b border-gray-300 shadow-lg bg-white"
            data-testid={MultiSelectDropDownTestID.ButtonContainer}
          >
            <button
              onClick={() => {
                setActiveIndexes([]);
                setShowDropdown(false);
                if (showsSearch) {
                  onSearch('');
                }
                onClear();
              }}
              className="text-gray-600 font-medium"
              data-testid={MultiSelectDropDownTestID.ClearButton}
            >
              Clear
            </button>
            <button
              onClick={() => {
                onChange(activeIndexes);
                setShowDropdown(false);
              }}
              className="bg-blue-200 text-white font-medium py-2 px-4 rounded"
              data-testid={MultiSelectDropDownTestID.SaveButton}
            >
              Save
            </button>
          </div>
        </div>
      )}
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        style={{ width: 'max-content' }}
        aria-label={`filter for ${placeholder}`}
        className={`
        ${
          activeIndexes.length > 0 && !showDropdown
            ? 'border-blue-200 bg-blue-100 text-blue-200'
            : ''
        } border rounded text-gray-800 placeholder-gray-600 font-medium flex flex-row justify-between items-center px-2 border-gray-300 py-1 w-56 h-full focus:outline-none`}
      >
        {placeholder}
        <IconDropDownArrow
          className={`
          ${showDropdown ? 'rotate-180 ' : ''}
          ${
            activeIndexes.length > 0 && !showDropdown
              ? 'text-blue-200 fill-current stroke-current'
              : 'text-gray-600 fill-current stroke-current'
          } rotate-90 ml-6 fill-current h-2 w-2`}
        />
      </button>
    </div>
  );
};
